<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { Porthole } from '@client/components/porthole';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { GroupsService } from '@client/services/groups';

  import { generatePastelColors } from '@common/utils/colors';

  export let config = {};
  export let field = '';
  export let exclusions = false;
  export let active = false;
  export let isClearable = true;
  export let onChange = () => {};

  let selected = [];
  let items = [];
  let originalItems = [];
  let previousFilterText = '';
  let filterText = '';
  let searching = false;
  let loading = false;

  const colorAssignments = {};

  const group = exclusions ? config.exclusions[field] : config[field];

  const definitions = {
    csvtreenodebindings: {
      method: 'getWorkdayManagers',
      label: exclusions ? $format('label.EXCLUDED.MANAGERS') : $format('label.INCLUDED.MANAGERS'),
      placeholder: exclusions
        ? $format('label.EXCLUDED.MANAGERS_PLACEHOLDER')
        : $format('label.INCLUDED.MANAGERS_PLACEHOLDER')
    },
    o365groupbindings: {
      method: 'getOfficeGroups',
      label: exclusions ? $format('label.EXCLUDED.GROUPS') : $format('label.INCLUDED.GROUPS'),
      placeholder: exclusions
        ? $format('label.EXCLUDED.GROUPS_PLACEHOLDER')
        : $format('label.INCLUDED.GROUPS_PLACEHOLDER')
    },
    googlecountrybindings: {
      method: 'getGoogleCountries',
      label: $format('label.INCLUDED.COUNTRIES'),
      placeholder: $format('label.INCLUDED.COUNTRIES_PLACEHOLDER')
    },
    googlegroupbindings: {
      method: 'getGoogleGroups',
      label: exclusions ? $format('label.EXCLUDED.GROUPS') : $format('label.INCLUDED.GROUPS'),
      placeholder: exclusions
        ? $format('label.EXCLUDED.GROUPS_PLACEHOLDER')
        : $format('label.INCLUDED.GROUPS_PLACEHOLDER')
    },
    googleorgbindings: {
      method: 'getGoogleOrgs',
      label: exclusions ? $format('label.EXCLUDED.ORGANIZATIONS') : $format('label.INCLUDED.ORGANIZATIONS'),
      placeholder: exclusions
        ? $format('label.EXCLUDED.ORGANIZATIONS_PLACEHOLDER')
        : $format('label.INCLUDED.ORGANIZATIONS_PLACEHOLDER')
    },
    oktagroupbindings: {
      method: 'getOktaGroups',
      label: exclusions ? $format('label.EXCLUDED.GROUPS') : $format('label.INCLUDED.GROUPS'),
      placeholder: exclusions
        ? $format('label.EXCLUDED.GROUPS_PLACEHOLDER')
        : $format('label.INCLUDED.GROUPS_PLACEHOLDER')
    }
  };

  const setGroupColor = (value, colors) => {
    if (!colorAssignments[value]) {
      colorAssignments[value] = colors.pop();
    }

    return colorAssignments[value];
  };

  const processGroupItem = (group, colors, selected) => {
    const { directMembersCount, total_reports, name, system, ...rest } = group;
    const firstname = system === 'workday' ? `${rest.first} ${rest.last}` : name;
    const managed = system === 'workday' ? total_reports : directMembersCount;
    const fieldId = system === 'workday' ? 'employee_id' : 'uuid';
    const id = rest[fieldId];
    const googleCountry = field === 'googlecountrybindings';
    const groupId = !googleCountry ? id : rest._id;
    const groupName = !googleCountry ? firstname : groupId;

    if (field === 'googlecountrybindings' && !rest._id) {
      return;
    }

    return {
      ...rest,
      name: groupName,
      id: groupId,
      label: groupName,
      color: setGroupColor(groupId, colors),
      total: !googleCountry ? managed : 0,
      value: groupId,
      type: field,
      checked: selected?.includes(groupId)
    };
  };

  const fetchAndProcessGroups = async (query, isSearch) => {
    const groups = await GroupsService[definitions[field].method]({
      bindings: group,
      query
    });

    const list = Array.isArray(groups) ? groups : [];

    const colors = generatePastelColors(list.length);

    const processedGroups = list.map((group) => processGroupItem(group, colors, selected)).filter(Boolean);

    if (isSearch) {
      processedGroups.forEach((item) => {
        if (!originalItems.some((existingItem) => existingItem.id === item.id)) {
          originalItems.push(item);
        }
      });
    } else {
      originalItems = processedGroups.reduce((uniqueItems, item) => {
        if (!uniqueItems.some((existingItem) => existingItem.id === item.id)) {
          uniqueItems.push(item);
        }

        return uniqueItems;
      }, []);
    }

    originalItems = [...originalItems].sort((a, b) => a.name.localeCompare(b.name));

    items = [...originalItems];
  };

  const getGroups = async ({ value = '' }) => {
    loading = true;

    selected = (group || []).map(({ id, employee_id }) => id || employee_id);

    try {
      await fetchAndProcessGroups(value);
    } finally {
      loading = false;
    }
  };

  const searchGroups = async () => {
    if (!filterText.length) {
      return;
    }

    if (!searching && previousFilterText !== filterText) {
      searching = true;

      previousFilterText = filterText;

      await fetchAndProcessGroups(filterText, true);

      searching = false;
    }
  };

  onMount(async () => await getGroups({ value: '' }));

  const onClear = ({ detail }) => {
    if (detail?.length) {
      selected = [];
    } else {
      selected = selected?.filter(({ value }) => value !== detail.value);
    }

    onSelect({ detail: selected || [] });
  };

  const onSelect = ({ detail = [] }) => {
    onChange(detail, field, exclusions);
  };

  $: if (!filterText.length) {
    items = [...originalItems];
  }

  $: totalGroups = originalItems.length;
  $: count = selected?.length || 0;
  $: active = count > 0;
</script>

<div class="header">{definitions[field].label} ({count}/{totalGroups})</div>

<Select
  bind:items
  bind:value={selected}
  on:change={onSelect}
  on:clear={onClear}
  on:filter={searchGroups}
  bind:filterText
  placeholder={definitions[field].placeholder}
  class="select-element"
  searchable
  multiple
  clearable={isClearable}
  {loading}
>
  <svelte:fragment slot="selection" let:selection>
    <span class="selection">
      {selection.label}
      {#if field !== 'googlecountrybindings' && field !== 'googleorgbindings'}
        ({selection.total})
      {/if}
    </span>
  </svelte:fragment>

  <svelte:fragment slot="item" let:item>
    <div class="item">
      <Porthole details={item} color={item.color} />
      <div class="item-detail">
        <div class="name">{item.name}</div>
        {#if field !== 'googlecountrybindings' && field !== 'googleorgbindings'}
          <div class="email">{$format('label.MEMBERS_TOTAL', { values: { total: item.total } })}</div>
        {/if}
      </div>
    </div>
  </svelte:fragment>

  <svelte:fragment slot="empty">
    {#if searching}
      <div class="empty">
        <Spinner show />
      </div>
    {:else}
      <div class="empty">
        {$format('label.NO_OPTIONS_AVAILABLE')}
      </div>
    {/if}
  </svelte:fragment>
</Select>

<style lang="scss" src="./group-selector.scss"></style>
